/**
 * CollapsibleSection — Fluent `Accordion` wrapper for content that should
 * collapse by default (heavy code blocks, debugging output, optional detail).
 *
 * Use when: the content is informative but not always needed. Saves vertical
 * real estate on narrow windows. For always-visible sections use `SectionCard`
 * or `SectionLabel`. For form groups use `FormSection`.
 *
 * Supports both uncontrolled (`defaultOpen`) and controlled
 * (`open` + `onOpenChange`) modes — the latter for URL-hash, "expand all"
 * buttons, or any external state coordination.
 */
import type { ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1Strong,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  panel: {
    paddingTop: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  description: {
    color: tokens.colorNeutralForeground3,
  },
});

interface CollapsibleSectionProps {
  title: ReactNode;
  description?: ReactNode;
  /** Uncontrolled: open on first render. Defaults to false (collapsed). */
  defaultOpen?: boolean;
  /** Controlled: open state. Pair with `onOpenChange`. */
  open?: boolean;
  /** Controlled: fired when the user toggles the disclosure. */
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function CollapsibleSection({
  title,
  description,
  defaultOpen = false,
  open,
  onOpenChange,
  children,
}: CollapsibleSectionProps) {
  const styles = useStyles();
  const isControlled = open !== undefined;

  /* When controlled, drive Accordion via openItems; otherwise use defaultOpenItems. */
  const accordionProps = isControlled
    ? {
        openItems: open ? ['main'] : [],
        onToggle: (_: unknown, data: { openItems: string[] }) => {
          onOpenChange?.(data.openItems.includes('main'));
        },
      }
    : { defaultOpenItems: defaultOpen ? ['main'] : [] };

  return (
    <Accordion collapsible multiple {...accordionProps} className={styles.root}>
      <AccordionItem value="main">
        <AccordionHeader expandIconPosition="end">
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Body1Strong>{title}</Body1Strong>
            {description ? <Caption1 className={styles.description}>{description}</Caption1> : null}
          </div>
        </AccordionHeader>
        <AccordionPanel>
          <div className={styles.panel}>{children}</div>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
